import { getSymbolAndMarketsNoUnifiedLiquidity } from '@src/utils/symbolAndMarketsNoUnifiedLiquidity';
import { canViewDeepDive, MixpanelEvent, useMarketAccountsContext, useMixpanel, type Security } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_LAYOUT_ID } from '../components/AppLayout/Layouts/tokens';
import { getNavGroupSlug } from '../components/NavigationSidebar/spacesNavPath';
import { getDeepDiveRoute } from '../containers/Routes/routes';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useCurrentAppLayoutId } from './useAppLayoutId';

export function useNavigateToDeepDive() {
  const navigate = useNavigate();
  const mixpanel = useMixpanel();
  const { marketAccountsByName } = useMarketAccountsContext();
  const layoutId = useCurrentAppLayoutId() ?? DEFAULT_LAYOUT_ID;
  const { layoutConfigs } = useAppLayoutConfig('trading');
  const navGroupName = useMemo(() => getNavGroupSlug(layoutConfigs, layoutId), [layoutConfigs, layoutId]);

  return useCallback(
    (
      security: Security,
      marketAccountSelections: string[] | undefined,
      marketSelections: string[] | undefined,
      aggregation: string | undefined
    ) => {
      if (!canViewDeepDive(security)) {
        return;
      }

      mixpanel.track(MixpanelEvent.OpenDeepDive);
      // [UI-6754] Until deepdive supports unified liquidity, remove those marketaccounts.
      const { marketAccounts: narrowedMarketAccounts, markets: narrowedMarkets } =
        getSymbolAndMarketsNoUnifiedLiquidity({
          security,
          marketAccountSelections,
          marketAccountsByName,
          marketSelections,
        });
      navigate(
        getDeepDiveRoute({
          symbol: security.Symbol,
          marketAccountNames: narrowedMarketAccounts,
          marketNames: narrowedMarkets,
          aggregation,
          layoutId,
          navGroupName,
        })
      );
    },
    [mixpanel, marketAccountsByName, navigate, layoutId, navGroupName]
  );
}
